import type { MapStoreState } from "./store";
import { decode, encode, emptyUrlState } from "./urlState";
import type { UrlState } from "./urlState";
import type { ResolvedOptions } from "./options";

export type UrlBindingSource = {
  state(): MapStoreState;
  selectedKeys(): string[];
  options: ResolvedOptions;
};

export type UrlBindingTarget = {
  apply(state: UrlState, previous: UrlState | null): void;
};

export type UrlBinding = {
  current(): UrlState;
  read(): UrlState;
  write(): void;
  dispose(): void;
};

export function urlStateOf(source: UrlBindingSource): UrlState {
  const state = source.state();
  const info = state.info;
  const result = emptyUrlState();

  result.maps = source.options.layers.maps;
  result.layers = source.options.layers.layersOffered;
  result.on = source.selectedKeys();
  result.zoom = state.view.zoom;
  result.center = state.view.center;
  result.objectIds = [...state.selection.ids];
  result.feature = info.kind === "tile" ? { layer: info.feature.hit.layer, id: info.feature.hit.id } : info.kind === "loading" && info.hit !== null ? { layer: info.hit.layer, id: info.hit.id } : null;
  result.draw = state.draw.geometry.mode;
  result.wkt = state.draw.geometry.wkt;
  result.query = state.search.term.trim().length > 0 ? state.search.term : null;
  result.lang = state.langId === 2 ? "en" : "hu";
  result.theme = state.theme;
  result.chrome = source.options.chrome === "full" ? null : source.options.chrome;
  result.panel = state.panel;
  result.tab = state.panel === "search" ? state.search.tab : null;

  return result;
}

export function pushWorthy(previous: UrlState | null, next: UrlState): boolean {
  if (previous === null) return false;

  const selectionOpened = next.objectIds.length > 0 && previous.objectIds.join(",") !== next.objectIds.join(",");
  const featureOpened = next.feature !== null && (previous.feature === null || previous.feature.layer !== next.feature.layer || previous.feature.id !== next.feature.id);

  return selectionOpened || featureOpened;
}

export function createUrlBinding(source: UrlBindingSource, target: UrlBindingTarget, history: History = window.history, location: Location = window.location): UrlBinding {
  let last: UrlState | null = null;
  let applying = false;

  const read = (): UrlState => decode(location.search);

  const write = (): void => {
    if (applying) return;

    const next = urlStateOf(source);
    const search = encode(next, location.search);

    if (last !== null && encode(last, location.search) === search) return;

    const url = `${location.pathname}${search}${location.hash}`;

    if (pushWorthy(last, next)) history.pushState({ fxmap: true }, "", url);
    else history.replaceState({ fxmap: true }, "", url);

    last = next;
  };

  const onPopState = () => {
    const state = read();

    applying = true;

    try {
      target.apply(state, last);
    } finally {
      applying = false;
    }

    last = urlStateOf(source);
  };

  window.addEventListener("popstate", onPopState);

  return {
    current: () => last ?? urlStateOf(source),
    read,
    write,
    dispose: () => window.removeEventListener("popstate", onPopState)
  };
}
